/**
 * useInteraction — view- and input-agnostic interaction core (navigation + editing).
 *
 * Sits on useCollection (organization) + useSelection (active item/field + selection)
 * and adds what neither owns: navigation between item×field positions, and the edit
 * lifecycle (which position is editing + its draft). No DOM, no input handling: the
 * per-view adapter (useGridInteraction) owns the DOM and maps keys/gestures to these
 * intents. On commit it PERSISTS the draft via the injected `updateItem` (the DATA
 * layer's optimistic write) — it owns the commit→persist trigger, not the write
 * itself. Cells only report the draft (`update:draft`) and request mode transitions
 * (`edit`/`commit`/`cancel`); they no longer emit their own persist.
 *
 * The active position is selection.activeItemKey + activeFieldKey. The move*
 * functions translate a direction/advance intent into a position change: they
 * choose collapse / extend / cycle, delegating the position math to nextInGrid
 * (whole grid) or cycleWithinSpan (inside a span).
 */
import { type InjectionKey, type Ref, ref } from "vue"
import {
  type Field,
  isJunctionField,
  isReferenceField,
  isSelectField,
  referenceEntities,
} from "@auger/core/fields"
import type { ItemKey } from "@auger/ui/composables/useCollection"

export type Direction = "up" | "down" | "left" | "right"

/** A reference target: an id, plus whatever the entity carries. */
type Entity = { id: number | string; [k: string]: unknown }

/** Writes a reference delta to one cell; GridElement provides it for its cells. */
export type CellWriter = (
  itemKey: ItemKey,
  fieldKey: string,
  delta: { add?: Entity[]; remove?: Entity[] }
) => Promise<unknown>
export const CELL_WRITER: InjectionKey<CellWriter> = Symbol("cell-writer")

/** What GridCell gives the field it hosts: its row, and `writer()`, a writer
 *  pinned to the row and field it is called for — a cell can be recycled onto
 *  another row before an upload it started finishes. Null when read-only. */
export interface CellContext {
  item: () => Record<string, unknown>
  writer: () => ((delta: { add?: Entity[]; remove?: Entity[] }) => Promise<unknown>) | null
}
export const CELL_CONTEXT: InjectionKey<CellContext> = Symbol("cell-context")

/** Deps the core needs from the DATA layer. `updateItem` is the host's
 *  optimistic write (`useRecords`/`useList`); the core calls it on commit but
 *  doesn't own it. */
export interface InteractionOptions {
  updateItem?: (id: number, fieldKey: string, value: unknown) => unknown
}

// The slice of useCollection the core reads.
export interface CollectionForInteraction<T> {
  sortedItems: Ref<T[]>
  sortedFields: Ref<Field[]>
  getItemKey: (item: T) => ItemKey
}

// The slice of useSelection the core reads + drives.
export interface SelectionForInteraction {
  // The active item & field.
  activeItemKey: Ref<ItemKey | null>
  activeFieldKey: Ref<string | null>
  isActive: (itemKey: ItemKey, fieldKey: string) => boolean
  // Move-only (preserves spans) — used by within-range cycling.
  activate: (itemKey: ItemKey, fieldKey: string) => void

  // Selection management (anchor + range)
  select: (itemKey: ItemKey, fieldKey: string) => void
  extendSelection: (itemKey: ItemKey, fieldKey: string) => void
  hasSelectionRange: Ref<boolean>
  deselectAll: () => void
  // Clear the active item/field only (preserves spans).
  deactivate: () => void

  // Item/field-axis writers (key-based). selection enforces the single-axis
  // invariant: an item-wise write drops field spans and vice versa.
  selectItem: (key: ItemKey) => void
  toggleItem: (key: ItemKey) => void
  selectField: (fieldKey: string) => void

  // Ordered axes (the logical sequences traversal walks) + active-range reads.
  // These let interaction own the walking while selection keeps owning the span
  // representation: `activeExtent` is the moving corner shift-extend grows from;
  // `getActiveSpanRect` is the materialized range cycling walks.
  orderedItemKeys: Ref<ItemKey[]>
  orderedFieldKeys: Ref<string[]>
  activeExtent: Ref<{ itemKey: ItemKey | null; fieldKey: string | null }>
  getActiveSpanRect: () => { items: ItemKey[]; fields: string[] } | null
}

/** How `beginEdit` treats a field:
 *  - "text"   open the inline editor, optionally seeded (type-to-edit)
 *  - "open"   open the field's own surface (select dropdown, link picker,
 *             boolean toggle) — the cell owns the edit and persists via @update
 *  - "none"   not editable; caller falls back (e.g. Enter advances instead) */
export type ActivationKind = "text" | "open" | "none"

export function useInteraction<T extends Record<string, unknown> = Record<string, unknown>>(
  collection: CollectionForInteraction<T>,
  selection: SelectionForInteraction,
  options: InteractionOptions = {}
) {
  const { updateItem } = options
  // Edit state. The edited position is always the active item & field, so this is
  // just a flag plus the in-progress draft the cell binds to.
  const isEditing = ref(false)
  const draft = ref<unknown>(null)

  // ---- Active item & field --------------------------------------------------
  // Re-exposed (same refs) so the view adapter can locate/scroll to the active
  // position without reaching into selection.
  const { activeItemKey, activeFieldKey } = selection

  function fieldFor(fieldKey: string): Field | undefined {
    return collection.sortedFields.value.find((f) => f.key === fieldKey)
  }

  // ---- Active-move chokepoint -----------------------------------------------
  // Every active-move funnels through here, so a move always ends any open edit.
  // That makes `isEditing ⇒ the active item/field hasn't moved since the edit
  // began` a structural invariant, not something a watcher reconciles — why
  // editing and navigation are one concern here. Views call these, not
  // selection.select/extendSelection.
  function setActive(itemKey: ItemKey, fieldKey: string) {
    isEditing.value = false
    draft.value = null
    selection.select(itemKey, fieldKey)
  }

  // Extend can't happen mid-edit; routing it here keeps one active-move API.
  function extendActive(itemKey: ItemKey, fieldKey: string) {
    selection.extendSelection(itemKey, fieldKey)
  }

  // ---- Traversal ------------------------------------------------------------
  // Selection owns the span data + ordered axes; this layer walks them.
  // `nextInRect`/`nextInGrid` are pure; the nav ops below apply the result
  // through `setActive`/`extendActive`.

  /** The next cell one step from `(itemKey, fieldKey)` within the ordered
   *  `items` × `fields` rectangle, or null when there's no next cell.
   *    - `axis`        the axis walked — "field" across, "item" down.
   *    - `wrapFields`  at a field edge, carry into the item axis (not stop).
   *    - `wrapItems`   at an item edge, loop to the other end (not stop).
   *  An edge with its wrap flag off returns null. Pure. */
  function nextInRect(
    items: readonly ItemKey[],
    fields: readonly string[],
    itemKey: ItemKey,
    fieldKey: string,
    {
      axis,
      forward,
      wrapItems = false,
      wrapFields = false,
    }: { axis: "item" | "field"; forward: boolean; wrapItems?: boolean; wrapFields?: boolean }
  ): { itemKey: ItemKey; fieldKey: string } | null {
    const row = items.indexOf(itemKey)
    const col = fields.indexOf(fieldKey)
    if (row === -1 || col === -1) return null

    const step = forward ? 1 : -1
    const primaryLen = axis === "field" ? fields.length : items.length
    const secondaryLen = axis === "field" ? items.length : fields.length
    const wrapPrimary = axis === "field" ? wrapItems : wrapFields
    const wrapSecondary = axis === "field" ? wrapFields : wrapItems
    let p = (axis === "field" ? col : row) + step
    let s = axis === "field" ? row : col

    if (p < 0 || p >= primaryLen) {
      if (!wrapSecondary) return null
      p = (p + primaryLen) % primaryLen
      s += step
      if (s < 0 || s >= secondaryLen) {
        if (!wrapPrimary) return null
        s = (s + secondaryLen) % secondaryLen
      }
    }

    const r = axis === "field" ? s : p
    const c = axis === "field" ? p : s
    return { itemKey: items[r]!, fieldKey: fields[c]! }
  }

  /** `nextInRect` over the whole grid (every ordered item × field). */
  function nextInGrid(
    itemKey: ItemKey,
    fieldKey: string,
    opts: { axis: "item" | "field"; forward: boolean; wrapItems?: boolean; wrapFields?: boolean }
  ): { itemKey: ItemKey; fieldKey: string } | null {
    return nextInRect(
      selection.orderedItemKeys.value,
      selection.orderedFieldKeys.value,
      itemKey,
      fieldKey,
      opts
    )
  }

  /** Step the active item/field to the next/prev position WITHIN its range,
   *  wrapping at the range's edges, span intact. `byField` walks items first
   *  (down-then-across, Enter-style); else fields first (Tab-style). Returns
   *  false (unchanged) when not inside a stretched span — caller falls back to
   *  whole-grid nav. Uses selection's `activate` (move-only, preserves spans). */
  function cycleWithinSpan(forward: boolean, byField = false): boolean {
    const rect = selection.getActiveSpanRect()
    if (!rect) return false
    const target = nextInRect(
      rect.items,
      rect.fields,
      activeItemKey.value as ItemKey,
      activeFieldKey.value as string,
      { axis: byField ? "item" : "field", forward, wrapItems: true, wrapFields: true }
    )
    if (!target) return false
    // Move-only (selection.activate) — keeps the span. Clears the edit here too,
    // so within-range advance ends an edit like every other move.
    isEditing.value = false
    draft.value = null
    selection.activate(target.itemKey, target.fieldKey)
    return true
  }

  // ---- Navigation -----------------------------------------------------------

  /** Land on the first position (top-left). */
  function activateFirst(): { itemKey: ItemKey; fieldKey: string } | null {
    const item = collection.sortedItems.value[0]
    const field = collection.sortedFields.value[0]
    if (!item || !field) return null
    const itemKey = collection.getItemKey(item)
    setActive(itemKey, field.key)
    return { itemKey, fieldKey: field.key }
  }

  /** Move the active position one step along an axis. `{ extend: true }` grows the
   *  range's extent from the active span's moving corner, leaving the active
   *  position at the anchor; a plain move collapses onto the new position
   *  (Excel/Sheets). No-op at an edge; with nothing active, lands on first. */
  function advance(dir: Direction, opts: { extend?: boolean } = {}) {
    if (activeItemKey.value == null || activeFieldKey.value == null) return activateFirst()
    // One axis, stop at the edge: no wrap, no carry.
    const axis = dir === "left" || dir === "right" ? "field" : "item"
    const forward = dir === "down" || dir === "right"
    // A plain move steps from the active position; an extend steps from the span's
    // current extent (the moving corner) so repeated extends keep growing.
    const ext = selection.activeExtent.value
    const fromItem = (opts.extend ? ext.itemKey : null) ?? activeItemKey.value!
    const fromField = (opts.extend ? ext.fieldKey : null) ?? activeFieldKey.value!
    const target = nextInGrid(fromItem, fromField, { axis, forward })
    if (!target) return null
    if (opts.extend) extendActive(target.itemKey, target.fieldKey)
    else setActive(target.itemKey, target.fieldKey)
    return target
  }

  /** Tab-style advance (fields first). In a span: cycle within it, span preserved.
   *  No span: walk the whole grid — across fields, carry to the next item, loop at
   *  the ends — collapsing onto the new position. */
  function advanceField(forward: boolean) {
    if (activeItemKey.value == null || activeFieldKey.value == null) return activateFirst()
    if (selection.hasSelectionRange.value && cycleWithinSpan(forward)) {
      return { itemKey: activeItemKey.value!, fieldKey: activeFieldKey.value! }
    }
    const target = nextInGrid(activeItemKey.value!, activeFieldKey.value!, {
      axis: "field",
      forward,
      wrapItems: true,
      wrapFields: true,
    })
    if (!target) return null
    setActive(target.itemKey, target.fieldKey)
    return target
  }

  /** The Enter-shaped advance (items first), driving the data-entry loop. In a
   *  span: cycle within it, span preserved. No span: a plain downward step that
   *  stops at the grid's bottom — unlike Tab, it does not loop. */
  function advanceItem(forward = true) {
    if (activeItemKey.value == null || activeFieldKey.value == null) return activateFirst()
    if (selection.hasSelectionRange.value && cycleWithinSpan(forward, true)) {
      return { itemKey: activeItemKey.value!, fieldKey: activeFieldKey.value! }
    }
    return advance(forward ? "down" : "up")
  }

  // ---- Edit lifecycle -------------------------------------------------------

  /** The field's TRUE value, for `beginEdit` to seed the draft. References edit as
   *  their target ENTITIES (the data layer decides how to persist); value fields as
   *  their raw value. */
  function draftSeedAt(itemKey: ItemKey, fieldKey: string): unknown {
    const field = fieldFor(fieldKey)
    const item = collection.sortedItems.value.find((it) => collection.getItemKey(it) === itemKey)
    if (!field || !item) return null
    if (isReferenceField(field) || isJunctionField(field)) {
      const entities = referenceEntities(field, item)
      return field.isArray ? entities : (entities[0] ?? null)
    }
    return field.rawValue(item)
  }

  /** Begin editing the active position. `draft` seeds from `seed` (type-to-edit)
   *  or the field's current value. Text fields open an inline editor; select/
   *  link/reference/boolean fields open their own surface (the cell owns the
   *  edit and persists via @update); non-editable is a no-op. Returns the kind
   *  so the caller can fall back (e.g. Enter-moves-down). */
  function beginEdit(seed?: unknown): ActivationKind {
    if (activeItemKey.value == null || activeFieldKey.value == null) return "none"
    const itemKey = activeItemKey.value!
    const fieldKey = activeFieldKey.value!

    // How the active field responds to activation (Enter / type / click-to-edit):
    //   "none" not editable (on this item); "open" the cell owns its own surface (boolean toggle,
    //   select/link/reference picker) + persists via @update; "text" inline editor.
    const field = fieldFor(fieldKey)
    const item = collection.sortedItems.value.find((it) => collection.getItemKey(it) === itemKey)
    const editable = !!field?.isEditable && (!item || (field.isEditableFor?.(item) ?? true))
    const kind: ActivationKind = !editable
      ? "none"
      : field.type === "boolean" ||
          isSelectField(field) ||
          isJunctionField(field) ||
          isReferenceField(field)
        ? "open"
        : "text"

    if (kind === "none") return "none"
    // The edit target is the active position, so edit state is just the flag +
    // the seeded draft. A typed key seeds only a text editor; a field that owns
    // its surface starts from its value (a "k" is no tag or file).
    draft.value = kind === "text" && seed !== undefined ? seed : draftSeedAt(itemKey, fieldKey)
    isEditing.value = true
    return kind
  }

  /** Update the in-progress value as the user types (the cell binds this). */
  function setDraft(value: unknown) {
    draft.value = value
  }

  /** Commit the edit in place: hand the draft to the injected `updateItem`, then
   *  leave edit mode — active position unchanged, no advance. Used by
   *  select/boolean/reference picks and outside-press. This layer is dumb: it
   *  doesn't diff — whether the value actually changed is `updateItem`'s call
   *  (re-committing the same value is a legitimate reset). The only guard is
   *  "was there an edit to commit" (`isEditing` or a set draft) so a merely-active
   *  position — no edit, null draft — doesn't fire a write that clears the field. */
  function commitEdit() {
    const itemKey = activeItemKey.value
    const fieldKey = activeFieldKey.value
    if (
      updateItem &&
      (isEditing.value || draft.value != null) &&
      itemKey != null &&
      fieldKey != null
    ) {
      // Consume the promise: updateItem is async and its write arbiters reject
      // on a refusal. `arbitrate` has already toasted by the time we get here,
      // so this only keeps the rejection from going unhandled.
      Promise.resolve(updateItem(Number(itemKey), fieldKey, draft.value)).catch((err: unknown) =>
        console.error("[interaction] commit failed:", err)
      )
    }
    isEditing.value = false
    draft.value = null
  }

  /** Discard the edit in place — no persist. */
  function cancelEdit() {
    isEditing.value = false
    draft.value = null
  }

  /** Commit, then step down to the next item (the text-cell data-entry loop:
   *  type, Enter, type, Enter). Only the text cell's Enter routes here; every
   *  other commit stays put. */
  function commitAndAdvance(forward = true) {
    commitEdit()
    advanceItem(forward)
  }

  /** Add or remove targets on a reference cell outside the edit lifecycle, for
   *  work that finishes after its editor has gone (an upload, a drop on a cell
   *  that isn't editing). The delta resolves against the item's entities at the
   *  moment of writing, so a late write never replays a stale set. */
  function write(
    itemKey: ItemKey,
    fieldKey: string,
    delta: { add?: Entity[]; remove?: Entity[] }
  ): Promise<unknown> {
    const field = fieldFor(fieldKey)
    const item = collection.sortedItems.value.find((it) => collection.getItemKey(it) === itemKey)
    if (!updateItem || !field || !item) return Promise.resolve(false)
    const removing = new Set((delta.remove ?? []).map((e) => String(e.id)))
    const kept = referenceEntities(field, item).filter((e) => !removing.has(String(e.id)))
    const added = (delta.add ?? []).filter((e) => !kept.some((k) => String(k.id) === String(e.id)))
    const next = field.isArray ? [...kept, ...added] : (added.at(-1) ?? kept[0] ?? null)
    return Promise.resolve(updateItem(Number(itemKey), fieldKey, next))
  }

  // ---- Mutation intents -----------------------------------------------------
  // Input-agnostic intents the adapter dispatches; the adapter owns which key (or
  // gesture) maps to each (e.g. Delete → clearSelected, Escape → deselectAll).

  /** Clear the selected fields' values. NOT IMPLEMENTED: clearing is a
   *  per-field-type operation (a value field nulls; a link/tag/contact field
   *  must remove its junction rows, not null the stored id), so a generic
   *  `updateItem(key, null)` would orphan rows on those types. Deferred until the
   *  field registry grows a `clearValue` seam (the relation-row remove path).
   *  No-op with a one-time warning until then. */
  let warnedClear = false
  function clearSelected() {
    if (warnedClear) return
    warnedClear = true
    console.warn(
      "[useInteraction] clearSelected is not implemented — clearing must be " +
        "per-field-type (value → null, junction → remove rows). Awaiting a " +
        "field-registry clearValue seam."
    )
  }

  return {
    // Edit state
    draft,
    isEditing,

    // Active item & field — re-exposed so the DOM adapter can locate/scroll to it
    // (the adapter holds `interaction`, not `selection`).
    activeItemKey,
    activeFieldKey,

    // Active mutations
    setActive,
    extendActive,
    advance,
    advanceField,
    advanceItem,
    activateFirst,

    // Edit lifecycle
    beginEdit,
    setDraft,
    commitEdit,
    cancelEdit,
    commitAndAdvance,
    write,

    // Mutation intents
    clearSelected,
    // Clear the whole selection (Escape). Re-exposed from selection so adapters,
    // which hold only `interaction`, can reach it through the facade.
    deselectAll: selection.deselectAll,
    // Drop the active cell, keeping the row/column selection.
    deactivate: selection.deactivate,
  }
}
